/**
 * Offline action queue for drivers with no cell signal.
 *
 * At-scene photos and job status transitions are saved to IndexedDB when
 * the network is unreachable, then replayed FIFO (photos first, then the
 * status change that depended on them) as soon as connectivity returns —
 * via the browser `online` event, a 20s retry timer, or a manual retry tap.
 *
 * Drop policy on replay:
 *   - network error        → stop, keep everything, retry later
 *   - 401/403 (auth lapse) → stop, keep everything (re-login will resume)
 *   - other 4xx/5xx        → drop that item (server permanently rejected it)
 */
import { API, uploadJobPhoto, updateJobStatus } from "./api";

const DB_NAME = "nts_offline_v1";
const STORE = "queue";
const SYNC_TAG = "nts-offline-flush";

const listeners = new Set();
let flushing = false;
let autoFlushStarted = false;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Background Sync (Android Chrome): the service worker drains the queue
// even after the tab is closed. Items carry the API base + token because a
// service worker can't read localStorage.
function requestBackgroundSync() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.getRegistration()
    .then((reg) => reg?.sync?.register(SYNC_TAG))
    .catch(() => { /* unsupported (iOS) — foreground flush covers it */ });
}

async function addItem(item) {
  const db = await openDb();
  const id = await new Promise((resolve, reject) => {
    const t = db.transaction(STORE, "readwrite");
    const req = t.objectStore(STORE).add({
      ...item,
      createdAt: Date.now(),
      apiBase: API,
      token: localStorage.getItem("rescue_token"),
    });
    t.oncomplete = () => { db.close(); notify(); resolve(req.result); };
    t.onerror = () => { db.close(); reject(t.error); };
  });
  requestBackgroundSync();
  return id;
}

async function putItem(item) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, "readwrite");
    t.objectStore(STORE).put(item);
    t.oncomplete = () => { db.close(); resolve(); };
    t.onerror = () => { db.close(); reject(t.error); };
  });
}

export async function getPending() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).getAll();
    req.onsuccess = () => { db.close(); resolve(req.result || []); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

export async function removeItem(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, "readwrite");
    t.objectStore(STORE).delete(id);
    t.oncomplete = () => { db.close(); notify(); resolve(); };
    t.onerror = () => { db.close(); reject(t.error); };
  });
}

export async function pendingCount() {
  return (await getPending()).length;
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

async function notify() {
  try {
    const n = await pendingCount();
    listeners.forEach((cb) => cb(n));
  } catch {
    /* IDB unavailable — nothing to notify */
  }
}

/** axios error with no HTTP response = network unreachable */
export function isNetworkError(err) {
  return !!err && !err.response;
}

/** axios error with 401/403 status = auth lapse (token expired, JWT
 * secret rotated on deploy, admin revoked access, etc). We treat these
 * exactly like a network outage for the driver-facing UX — queue the
 * photo locally so the driver's on-scene minutes aren't wasted, then
 * drain the queue the moment they log back in. Reported Feb 2026:
 * a driver's shift outlasted their 24h JWT and they got a hard "Not
 * authenticated" toast for every photo, losing the on-scene proof. */
export function isAuthError(err) {
  const code = err?.response?.status;
  return code === 401 || code === 403;
}

export async function enqueuePhoto(jobId, fileOrBlob, filename) {
  return addItem({
    kind: "photo",
    jobId,
    blob: fileOrBlob,
    filename: filename || fileOrBlob.name || "photo.jpg",
    type: fileOrBlob.type || "image/jpeg",
  });
}

export async function enqueueStatus(jobId, status, photosSkipReason = null) {
  return addItem({ kind: "status", jobId, status, photosSkipReason });
}

/** Latest queued status per job — used to overlay onto server data so a
 * poll refresh doesn't visually revert a locally-advanced job. */
export async function getQueuedStatusByJob() {
  const map = new Map();
  for (const it of await getPending()) {
    if (it.kind === "status") {
      map.set(it.jobId, {
        status: it.status,
        ...(it.photosSkipReason ? { photos_skip_reason: it.photosSkipReason } : {}),
      });
    }
  }
  return map;
}

/** Purge every queued item (status advances AND photo uploads) for a
 * job id. Called when the server reports the job as terminal
 * (completed/cancelled) — those queued items would either fail on
 * replay (409/404) or, worse, resurrect a cancelled job by advancing it
 * back to en_route. This is the front-side half of the Feb 2026
 * A63608597 "ghost job jammed on driver's screen" fix. */
export async function purgeByJobId(jobId) {
  const items = await getPending();
  let purged = 0;
  for (const it of items) {
    if (it.jobId === jobId) {
      try {
        await removeItem(it.id);
        purged += 1;
      } catch {
        /* best-effort — skip and continue */
      }
    }
  }
  return purged;
}

async function flushItems() {
  const items = (await getPending()).sort((a, b) => a.id - b.id);
  for (const it of items) {
    try {
      if (it.kind === "photo") {
        const f = new File([it.blob], it.filename, { type: it.type });
        await uploadJobPhoto(it.jobId, f);
      } else if (it.kind === "status") {
        await updateJobStatus(it.jobId, it.status, it.photosSkipReason || null);
      }
      // Sent → delete the local copy so photos never pile up on the device.
      await removeItem(it.id);
    } catch (err) {
      if (isNetworkError(err)) return; // still offline — retry later
      const code = err?.response?.status;
      if (code === 401 || code === 403) return; // auth lapse — resume after re-login
      if (code >= 500 && (it.serverFails || 0) < 4) {
        // Server hiccup (e.g. mid-deploy 502) — keep it, retry later.
        await putItem({ ...it, serverFails: (it.serverFails || 0) + 1 });
        return;
      }
      console.warn("Offline sync item rejected by server — dropped:", it.kind, it.jobId, err?.response?.data);
      await removeItem(it.id);
    }
  }
}

export async function flush() {
  if (flushing) return;
  flushing = true;
  try {
    // Same lock name as the service worker's background sync so the two
    // never upload the same photo twice.
    if (navigator.locks) await navigator.locks.request(SYNC_TAG, flushItems);
    else await flushItems();
  } finally {
    flushing = false;
    notify();
  }
}

export function startAutoFlush() {
  if (autoFlushStarted) return;
  autoFlushStarted = true;
  flush(); // send anything left over the moment the app opens
  window.addEventListener("online", () => flush());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") flush();
  });
  navigator.serviceWorker?.addEventListener("message", (e) => {
    if (e.data?.type === "offline-queue-changed") notify();
  });
  setInterval(async () => {
    try {
      if ((await pendingCount()) > 0) flush();
    } catch {
      /* ignore */
    }
  }, 20000);
}
